import { faSearch } from "@fortawesome/free-solid-svg-icons";
import Icon from "components/components/Icon/Icon";
import { ApiData } from "models/apiDocumentation/apiDocumentationModels";
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select, {
  components,
  DropdownIndicatorProps,
  MultiValue,
  SingleValue,
} from "react-select";
import { InputActionMeta } from "react-select/dist/declarations/src/types";
import colours from "styles/colours";
import {
  fuzzyApiFilter,
  SearchBarOption,
  seeAllResultsPage,
} from "utils/fuzzyApiHelpers";
import routeConstants, { apiDocsRouteSuffixes } from "utils/routeConstants";

import { augmentTheme, customStyles } from "./style";

export const apiSearchQueryParam = "query";

const DropdownIndicator: React.FC<DropdownIndicatorProps<SearchBarOption>> = (
  props: DropdownIndicatorProps<SearchBarOption>
) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <Icon iconName={faSearch} colour={colours.mediumGrey} />
      </components.DropdownIndicator>
    )
  );
};

const placeholder = (
  <span id="api-search-placeholder" data-test-id="api-search-placeholder">
    Search by endpoint
  </span>
);

interface Props {
  apiData: ApiData;
  searchBarKey: number;
  setSearchBarKey: (searchBarKey: number) => void;
}

const ApiSearchBar: React.FC<Props> = ({
  apiData,
  searchBarKey,
  setSearchBarKey,
}) => {
  const navigate = useNavigate();
  const allOptions = apiData.endpoints;
  const [searchParams] = useSearchParams();

  const [filterString, setFilterString] = useState<string>(
    searchParams.get(apiSearchQueryParam) ?? ""
  );
  const filteredOptions: SearchBarOption[] = fuzzyApiFilter(
    allOptions,
    filterString,
    true
  );

  const onInputChange = (input: string, { action }: InputActionMeta): void => {
    if (action === "input-change") {
      // only set the input when the action that caused the
      // change equals to "input-change" and ignore the other
      // ones like: "set-value", "input-blur", and "menu-close"
      document
        .querySelectorAll("[class$=-option]")[0]
        ?.scrollIntoView({ block: "nearest" });
      // This ensures that if the user has scrolled down the menu and then
      // types a new character the menu scrolls up to the top again so they
      // can see the best match
      setFilterString(input);
    }
  };

  const onChange = (
    selectedOption: SingleValue<SearchBarOption> | MultiValue<SearchBarOption>
  ): void => {
    setSearchBarKey(searchBarKey + 1);
    const endpointPath =
      (selectedOption as SearchBarOption).url === seeAllResultsPage.url
        ? `${seeAllResultsPage.url}?${apiSearchQueryParam}=${filterString}`
        : `${apiDocsRouteSuffixes.endpoint}${
            (selectedOption as SearchBarOption).url
          }`;
    navigate(`${routeConstants.apiDocs}${endpointPath}`);
  };

  return (
    <div data-test-id="api-endpoint-search">
      <Select
        isMulti={false}
        options={filteredOptions}
        getOptionLabel={(option: SearchBarOption): string => option.displayText}
        placeholder={placeholder}
        inputValue={filterString}
        onInputChange={onInputChange}
        onChange={onChange}
        filterOption={null} // Fuse will have already done the filtering so react-select should show all results
        className="search-bar"
        theme={augmentTheme}
        styles={customStyles}
        components={{
          IndicatorSeparator: null,
          DropdownIndicator,
        }}
      />
    </div>
  );
};
export default ApiSearchBar;
